/* Which Momentum tools she has actually built something in.
 *
 * WHAT WAS WRONG (spec 5.12, R10)
 * The sidebar's Momentum progress bar credited the Offer Ladder and nothing
 * else, on a comment saying the other tools' tables "have no code reading them
 * yet". That was true in July. Since then Sites, Courses, Outreach, Meeting
 * Links and the Social Generator all read and write their own tables — so a
 * coach with twelve modules and six pages written saw the same bar as one who
 * had written nothing, and the one tool that did count made the bar look broken
 * rather than empty.
 *
 * WHY A COUNT AND NOT THE STORES
 * The stores live inside MomentumHub, which is only mounted while she is on a
 * Momentum panel. The sidebar is on screen the whole time. Reading the stores
 * from here would mean either loading five full tables into App for a tick, or
 * a bar that reads zero until she happens to open Momentum — which is the bug.
 *
 * THE COST, said plainly (check 4): one `head` count per table, five of them.
 * A head count returns no rows — it is a `Content-Range` header and nothing
 * else. They fire at sign-in, and again when a caller asks (`reload`) with a
 * floor of REFRESH_AFTER_MS between runs, so navigating around cannot turn into
 * a stream of requests. Zero per render, per keystroke and per prompt.
 *
 * WHY IT REFRESHES AT ALL. A count read once at sign-in goes stale the moment
 * she writes her first page — and a tick that quietly does not appear is the
 * exact failure mode CLAUDE.md's check 2 is written against. So App asks again
 * as she moves between panels, and the floor keeps that cheap.
 *
 * WHAT "BUILT" MEANS: one saved row. Not "finished" — the bar counts tools she
 * has started, the way the Offer Ladder always did. Done-ness is a different
 * question with a different owner (spec R6, step 5), and this is deliberately
 * not it.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useUser } from "@clerk/clerk-react";
import { useSupabase } from "../supabaseClient";

/* The nav id each table backs, so the sidebar's `it.id` is what comes back.
 * Ids come from BUCKETS in MomentumHub.jsx; tables from each tool's store.
 *
 * NOT IN HERE:
 *   offers        — App already holds the whole array for the ladder, so it is
 *                   answered without a query. See builtToolIds below.
 *   content_plan  — a locked-output chat, not a table (and slated to retire,
 *                   spec 6.11). Its state is an `outputs` question.
 *   the soon tools — their tables exist from 0001 and nothing writes them.
 *                   Counting them would report 0 forever, which is honest but
 *                   useless; they are out of the denominator too (`status`).
 */
export const BUILT_TABLES = [
  { id: "website", table: "website_pages" },
  { id: "lesson_plan", table: "lesson_plans" },
  { id: "outbound", table: "outbound_sequences" },
  // Live 2026-09-16. Without this row the tool joins the denominator and can
  // never be ticked, so every coach's Momentum bar would drop a notch.
  { id: "salescall", table: "call_scripts" },
  // Admin-only while it is tested; the sidebar counts it only where it is live.
  { id: "talks", table: "presentations" },
  { id: "ctas", table: "ctas" },
  { id: "social", table: "social_posts" },
  // Live 2026-09-17. Ticked once she has jotted anything down.
  { id: "library", table: "library_items" },
];

/**
 * The tools with something saved, as a Set of nav ids.
 *
 * Pure, and separate from the fetching, because "does this tool count as built"
 * is the question the tick and the progress bar BOTH ask — and the two reading
 * it differently is exactly the class of bug the sidebar has had before.
 *
 * @param offers the array App already holds (useOffers), not a count.
 * @param counts `{ [navId]: number }` from useMomentumCounts. A missing entry
 *               means "not answered yet", which reads as not built — never as
 *               empty, because a count that hasn't arrived is not a zero.
 */
export function builtToolIds({ offers = [], counts = {} } = {}) {
  const built = new Set();
  if ((offers || []).length > 0) built.add("offer_ladder");
  for (const { id } of BUILT_TABLES) {
    if ((counts?.[id] || 0) > 0) built.add(id);
  }
  return built;
}

/**
 * The counts that came back, as `{ [navId]: number }`, leaving out any table
 * that failed — or null when every one did.
 *
 * ONE FAILED TABLE MUST NOT BLANK THE REST. This used to throw on the first
 * error, so a single missing table took every tick in the tree with it. A new
 * tool ships before its migration is run (Call Scripts and supabase/0020,
 * 2026-09-16), so "one table does not exist yet" is a normal state, and it
 * should cost that tool's tick only. A left-out id reads as not built, never
 * as a zero (see builtToolIds).
 */
export function mergeCounts(results = []) {
  const out = {};
  for (const r of results || []) {
    if (r?.error) {
      console.error(`Failed to count Momentum table for ${r.id}:`, r.error);
      continue;
    }
    out[r.id] = r.count || 0;
  }
  return Object.keys(out).length ? out : null;
}

/* The floor between two runs of the counts. Long enough that clicking down the
   nav costs one run, short enough that writing a page and stepping to the next
   panel shows the tick. */
export const REFRESH_AFTER_MS = 20000;

/**
 * How many rows she has in each Momentum table.
 * `{ counts, loaded, refresh, reload }`.
 *
 * `enabled` is the Momentum access flag: a coach without Momentum has no tree to
 * tick and should not be sending five queries for it.
 */
export function useMomentumCounts({ enabled = true } = {}) {
  const { user } = useUser();
  const getSupabase = useSupabase();
  const [counts, setCounts] = useState({});
  const [loaded, setLoaded] = useState(false);
  // When the counts last went out, so a refresh can decline a run that would
  // only repeat one that is seconds old.
  const lastAt = useRef(0);

  // Same shape as recordStore: getSupabase is a fresh closure every render, so
  // depending on it directly would re-fire this forever.
  const getSb = useRef(getSupabase);
  getSb.current = getSupabase;

  const load = useCallback(async ({ force = true } = {}) => {
    if (!user?.id || !enabled) return;
    if (!force && Date.now() - lastAt.current < REFRESH_AFTER_MS) return;
    lastAt.current = Date.now();
    try {
      const sb = await getSb.current();
      const results = await Promise.all(BUILT_TABLES.map(async ({ id, table }) => {
        /* `head: true` asks for the count and no rows at all. The `.eq` is a
           second belt over each table's RLS policy, the same as every store. */
        const { count, error } = await sb.from(table)
          .select("id", { count: "exact", head: true })
          .eq("clerk_user_id", user.id);
        return { id, count, error };
      }));
      const merged = mergeCounts(results);
      if (!merged) throw results.find((r) => r.error)?.error || new Error("no counts");
      setCounts(merged);
      setLoaded(true);
    } catch (err) {
      /* A failed count is a bar that reads low, never a broken screen — so it
         is logged and swallowed. `loaded` stays false, which is what tells a
         caller the difference between "she has none" and "we don't know". */
      console.error("Failed to count Momentum tables:", err);
    }
  }, [user?.id, enabled]);

  useEffect(() => { load(); }, [load]);

  /* What App calls as she moves around. Throttled rather than forced: it is a
     tick in the sidebar, not something she is waiting on. */
  const refresh = useCallback(() => load({ force: false }), [load]);

  return { counts, loaded, refresh, reload: load };
}
